import type { BillingProductKey } from '../../supabase/functions/_shared/billingPolicy';
import { emptyStoreEntitlement, type PurchasesAdapter, type StoreEntitlement } from './purchasesContract';

// The web version's purchase lane (Owner decision list 2026-10-19 item 39): RevenueCat Web Billing (Stripe)
// through a hosted Web Purchase Link. The page is opened for the SIGNED-IN account's id, so the purchase lands
// on the same RevenueCat customer as the iPhone and Android apps; the server reads it from RevenueCat like any
// store purchase (supabase/functions/_shared/billing/revenueCat.ts). This page never sees a card number.
//
//   https://pay.rev.cat/<link token>/<app user id>?package_id=<package>&skip_purchase_success=true
//
// After paying, RevenueCat sends the browser back to the web app's address with `?purchase=done` (set in the
// RevenueCat dashboard, docs/web/WEB-BILLING-SETUP.md); src/state/webPurchaseReturn.ts picks that up.
// The e-mail is NOT put in the address (it would sit in logs and history): the payment page asks for it.
//
// Only the web build ever uses this. The iPhone / Android apps keep their store lane and contain no link to
// the web price (App Store Review 3.1.1 / 3.1.3; Owner item 39).

// The packages of the RevenueCat offering the link shows (one package per product).
export const WEB_PACKAGE_IDS: Readonly<Record<BillingProductKey, string>> = Object.freeze({
  single: 'web_single',
  monthly: '$rc_monthly',
  annual: '$rc_annual',
  addOn: 'web_pack3'
});

export const WEB_PURCHASE_MARK_KEY = 'yanwushi-v2:web-purchase:v1';

export function webPurchaseLink(): string {
  return ((import.meta.env.VITE_RC_WEB_PURCHASE_LINK as string | undefined) ?? '').trim().replace(/\/$/, '');
}

export function webCheckoutUrl(link: string, appUserId: string, product: BillingProductKey): string {
  const params = new URLSearchParams({ package_id: WEB_PACKAGE_IDS[product], skip_purchase_success: 'true' });
  return `${link}/${encodeURIComponent(appUserId)}?${params.toString()}`;
}

export function createWebPurchasesAdapter(options: { link: () => string; navigate: (url: string) => void }): PurchasesAdapter {
  let appUserId: string | null = null;
  const entitlementOf = (): StoreEntitlement => ({ ...emptyStoreEntitlement(), appUserId });
  return {
    platform: 'web',
    configured: () => options.link().length > 0,
    async identify(userId) {
      if (options.link().length === 0) return { ok: false, code: 'notConfigured', detail: 'VITE_RC_WEB_PURCHASE_LINK is not set' };
      appUserId = userId;
      // The web holds no receipts of its own: what this account owns is the server's answer.
      return { ok: true, entitlement: entitlementOf() };
    },
    async entitlement() {
      return null;
    },
    async purchase(product) {
      const link = options.link();
      if (link.length === 0) return { kind: 'failed', code: 'notConfigured', detail: 'no web purchase link' };
      if (!appUserId) return { kind: 'failed', code: 'identity', detail: 'not identified' };
      // (The billing store has noted the product and the account's state for the return, webPurchaseReturn.ts.)
      options.navigate(webCheckoutUrl(link, appUserId, product));
      // The page is being left for the payment page; the answer comes back with the browser.
      return { kind: 'pending' };
    },
    async restore() {
      if (options.link().length === 0) return { ok: false, code: 'notConfigured', detail: 'no web purchase link' };
      return { ok: true, entitlement: entitlementOf() };
    },
    async presentOfferCodeSheet() {
      // Apple / Google offer codes do not exist on the web; the web has its own redemption codes (item 46).
      return { ok: false, code: 'notNative', detail: 'offer codes are a store feature' };
    },
    openManageSubscriptions(url) {
      // RevenueCat's customer portal for a web subscription (the server passes management_url on).
      if (!url) return false;
      window.open(url, '_blank', 'noopener');
      return true;
    },
    onEntitlementChange() {
      return () => undefined;
    }
  };
}

export const webPurchasesAdapter: PurchasesAdapter = createWebPurchasesAdapter({ link: webPurchaseLink, navigate: (url) => window.location.assign(url) });
